import React from "react";
import ImageGallery, {
  ReactImageGalleryItem,
  ReactImageGalleryProps,
} from "react-image-gallery";
import { IListing } from "../../lib/Listing";
import { ListingVitals } from "../Listing/ListingVitals";
import { EWsrvTransform, makeWsrvURL } from "../../lib/Media/util";
import { LeftNav } from "./LeftNav";
import { RightNav } from "./RightNav";
import { IMediaItem } from "../../lib/Media";
import "react-image-gallery/styles/css/image-gallery.css";

export interface IListingGalleryProps {
  showNav?: boolean;
  autoPlay?: boolean;
  showThumbs?: boolean;
  showFullscreen?: boolean;
  showCaptions?: boolean;
  className?: string;
  showListingVitals?: boolean;
  preset: EWsrvTransform;
  width: number;
  height: number;
  imageGalleryProps?: Partial<ReactImageGalleryProps>;
  listing: IListing;
  customLeftNav?: React.ReactNode;
  customRightNav?: React.ReactNode;
  loading?: "eager"|"lazy";
  thumbnailLoading?: "eager"|"lazy";
  quality?: string;
  qualityThumbnail?: string;
  qualityFullscreen?: string;
}

export const ListingGallery: React.FC<IListingGalleryProps> = ({
  imageGalleryProps,
  listing,
  showNav = true,
  autoPlay = false,
  showThumbs = true,
  showFullscreen = true,
  showCaptions = true,
  showListingVitals = false,
  width = 1200,
  height = 800,
  preset = EWsrvTransform.fill3x2,
  className,
  customLeftNav,
  customRightNav,
  loading,
  thumbnailLoading,
  quality,
  qualityThumbnail,
  qualityFullscreen,
}) => {
  let items: ReactImageGalleryItem[] = [];
  listing.images?.map((image: IMediaItem) => {
    let srcSet: string[] = [];
    let sizes: string[] = [];
    let stops = [400, 800, 1200, 1600, 2000, 2400];
    stops.map((w) => {
      srcSet.push(
        makeWsrvURL({
          originURL: image.thumbnailURL || image.url,
          preset: preset,
          width: w,
          height: Math.round((height / width) * w),
          quality: quality
        }) +
        " " +
        w +
        "w"
      );
      sizes.push("(max-width: " + w + "px) " + w + "px");
    });
    sizes.push("100vw");
    items.push({
      original: makeWsrvURL({
        originURL: image.url,
        preset: preset,
        width: width,
        height: height,
        quality: quality,
      }),
      //original: image.url,
      srcSet: srcSet.join(",") || undefined,
      originalWidth: width,
      originalHeight: height,
      originalAlt: image.caption,
      thumbnailAlt: image.caption,
      description: showCaptions ? image.caption : "",
      thumbnail: makeWsrvURL({
        originURL: image.thumbnailURL || image.url,
        preset: preset,
        width: Math.round((width / height) * 80),
        height: 80,
        quality: qualityThumbnail,
      }),
      //thumbnail: image.thumbnailURL || image.url,
      thumbnailWidth: Math.round((height / width) * 80),
      thumbnailHeight: 80,
      fullscreen: makeWsrvURL({
        originURL: image.url,
        preset: EWsrvTransform.limit,
        width: 2400,
        height: 2400,
        quality: qualityFullscreen,
      }),
      //fullscreen: image.url,
      loading:loading,
      thumbnailLoading: thumbnailLoading,
    });
  });
  return (
    <ImageGallery
      items={items}
      additionalClass={className}
      showNav={showNav}
      showThumbnails={showThumbs && items?.length > 1}
      showPlayButton={false}
      showFullscreenButton={showFullscreen}
      autoPlay={autoPlay}
      lazyLoad={true}
      renderCustomControls={
        showListingVitals
          ? () => (
            <ListingVitals
              listing={listing}
              composite={true}
              className={`absolute bottom-5 ${showFullscreen ? "right-20" : "right-5"
                } z-30 bg-black/70`}
            />
          )
          : undefined
      }
      renderLeftNav={(
        onClick: React.MouseEventHandler<HTMLElement>,
        disabled: boolean
      ) =>
        customLeftNav ? (
          React.cloneElement(customLeftNav as React.ReactElement, {
            onClick,
            disabled,
          })
        ) : (
          <LeftNav onClick={onClick} disabled={disabled} />
        )
      }
      renderRightNav={(
        onClick: React.MouseEventHandler<HTMLElement>,
        disabled: boolean
      ) =>
        customRightNav ? (
          React.cloneElement(customRightNav as React.ReactElement, {
            onClick,
            disabled,
          })
        ) : (
          <RightNav onClick={onClick} disabled={disabled} />
        )
      }
      {...imageGalleryProps}
    />
  );
};

export default ListingGallery;
